/**
 * custom-sale-countdown.js
 * CCC sale countdown block — counts down to the product's REAL sale end date
 * (product metafield meta.countdown, passed in as data-sale-end, ISO
 * YYYY-MM-DD). The sale runs until 11:59pm Sydney time on that date and the
 * timer hides itself at zero.
 *
 * Only visible while the selected variant has a compare-at price.
 *
 * History note: this block used to count a per-browser localStorage deadline
 * that silently reset forever — an evergreen fake-urgency timer. It now only
 * ever shows a genuine deadline; with no metafield set the block does not
 * render at all (gated in Liquid).
 */

import { ThemeEvents } from '@theme/events';

/**
 * Epoch ms for 23:59:59 Sydney time on the given calendar date, DST-correct:
 * the offset is resolved ON that date (a noon-UTC probe reports the offset in
 * force that evening, on both AEST/AEDT transition days too).
 * @param {string} isoDate — YYYY-MM-DD
 * @returns {number|null}
 */
function sydneyEndOfDay(isoDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate || '')) return null;

  const probe = new Date(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(probe.getTime())) return null;

  let offset = '+10:00';
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      timeZoneName: 'longOffset',
    }).formatToParts(probe);
    const name = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const m = name.match(/GMT([+-]\d{1,2}):?(\d{2})?/);
    if (m) {
      const hours = m[1].replace(/^([+-])(\d)$/, '$10$2');
      offset = `${hours}:${m[2] || '00'}`;
    }
  } catch {
    /* very old engines: fall back to AEST */
  }

  const end = new Date(`${isoDate}T23:59:59${offset}`);
  return Number.isNaN(end.getTime()) ? null : end.getTime();
}

class CccSaleCountdown extends HTMLElement {
  connectedCallback() {
    this._deadline = sydneyEndOfDay(this.dataset.saleEnd);

    this._variants = this._parseVariants();
    this._daysEl = this.querySelector('[data-ccc-days]');
    this._hoursEl = this.querySelector('[data-ccc-hours]');
    this._minutesEl = this.querySelector('[data-ccc-minutes]');
    this._secondsEl = this.querySelector('[data-ccc-seconds]');

    this._rafId = null;
    this._lastRenderedSecond = null;

    // No valid future deadline → never show, regardless of sale pricing.
    if (!this._deadline || this._deadline <= Date.now()) {
      this.hidden = true;
      return;
    }

    if (!this.hidden) {
      this._start();
    }

    this._boundVariantHandler = (event) => {
      if (!this._isSameProductContext(event)) return;

      const eventProductId = event.detail?.data?.productId;
      const thisProductId = this.dataset.productId;
      if (eventProductId && thisProductId && String(eventProductId) !== String(thisProductId)) {
        return;
      }

      const variantId = event.detail?.resource?.id;
      if (!variantId || !this._findVariant(variantId)) return;

      const isSale = this._variantHasSale(variantId);
      const live = this._deadline > Date.now();
      this.hidden = !(isSale && live);

      if (isSale && live) {
        this._start();
      } else {
        this._stop();
      }
    };

    document.addEventListener(ThemeEvents.variantUpdate, this._boundVariantHandler);
  }

  disconnectedCallback() {
    this._stop();
    if (this._boundVariantHandler) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._boundVariantHandler);
    }
  }

  _parseVariants() {
    try {
      const scriptEl = this.querySelector('[data-ccc-sale-variants]');
      return scriptEl ? JSON.parse(scriptEl.textContent) : [];
    } catch {
      return [];
    }
  }

  _findVariant(id) {
    for (const variant of this._variants) {
      if (String(variant.id) === String(id)) {
        return variant;
      }
    }

    return null;
  }

  _variantHasSale(id) {
    const variant = this._findVariant(id);
    if (!variant) return false;

    return variant.compare_at_price !== null && variant.compare_at_price > variant.price;
  }

  _isSameProductContext(event) {
    if (!(event.target instanceof HTMLElement)) return true;

    const sourceSection = event.target.closest('.shopify-section, dialog');
    const hostSection = this.closest('.shopify-section, dialog');

    return !sourceSection || !hostSection || sourceSection === hostSection;
  }

  _pad(value) {
    return value < 10 ? `0${value}` : String(value);
  }

  _tick = () => {
    const ms = this._deadline - Date.now();

    // Sale over: hide for good — never reset, never restart.
    if (ms <= 0) {
      this.hidden = true;
      this._stop();
      return;
    }

    const totalSec = Math.floor(ms / 1000);

    if (totalSec !== this._lastRenderedSecond) {
      this._lastRenderedSecond = totalSec;

      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      if (this._daysEl) this._daysEl.textContent = String(days);
      if (this._hoursEl) this._hoursEl.textContent = this._pad(hours);
      if (this._minutesEl) this._minutesEl.textContent = this._pad(minutes);
      if (this._secondsEl) this._secondsEl.textContent = this._pad(seconds);
    }

    this._rafId = requestAnimationFrame(this._tick);
  };

  _start() {
    if (!this._rafId) {
      this._rafId = requestAnimationFrame(this._tick);
    }
  }

  _stop() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}

if (!customElements.get('ccc-sale-countdown')) {
  customElements.define('ccc-sale-countdown', CccSaleCountdown);
}
